import { z } from 'zod';
import { laborRoleSchema } from './labor.js';

/**
 * Wave 97 — Workforce: named workers, time-punches, productivity targets.
 *
 * Role enum reuses the Wave 29 LaborRole so cross-module reports stay
 * consistent. A "punch" is one (worker, date, clock_in[, clock_out])
 * row — at most one OPEN punch per (worker, date).
 */

export const workerSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  fullName: z.string(),
  role: laborRoleSchema,
  dailyRateCents: z.number().int().min(0).nullable(),
  active: z.boolean(),
  createdAt: z.string(),
});
export type Worker = z.infer<typeof workerSchema>;

export const createWorkerSchema = z.object({
  projectId: z.string().min(1),
  fullName: z.string().trim().min(2).max(120),
  role: laborRoleSchema,
  dailyRateCents: z.number().int().min(0).nullable().optional(),
});
export type CreateWorkerRequest = z.infer<typeof createWorkerSchema>;

export const updateWorkerSchema = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  role: laborRoleSchema.optional(),
  dailyRateCents: z.number().int().min(0).nullable().optional(),
});
export type UpdateWorkerRequest = z.infer<typeof updateWorkerSchema>;

export interface TimePunch {
  id: string;
  workerId: string;
  workerName: string;
  role: z.infer<typeof laborRoleSchema>;
  projectId: string;
  punchDate: string;
  clockIn: string;
  clockOut: string | null;
  breakMinutes: number;
  netHours: number | null;
  costCents: number | null;
  notes: string | null;
}

export const clockInSchema = z.object({
  workerId: z.string().min(1),
  punchDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  clockIn: z.string().regex(/^\d{2}:\d{2}$/),
  notes: z.string().max(500).nullable().optional(),
});
export type ClockInRequest = z.infer<typeof clockInSchema>;

export const clockOutSchema = z.object({
  clockOut: z.string().regex(/^\d{2}:\d{2}$/),
  breakMinutes: z.number().int().min(0).max(480).optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type ClockOutRequest = z.infer<typeof clockOutSchema>;

export interface ProductivityTarget {
  id: string;
  projectId: string;
  role: z.infer<typeof laborRoleSchema>;
  metricLabel: string;
  targetPerHh: number;
}

export const upsertProductivityTargetSchema = z.object({
  projectId: z.string().min(1),
  role: laborRoleSchema,
  metricLabel: z.string().trim().min(2).max(80),
  targetPerHh: z.number().positive(),
});
export type UpsertProductivityTargetRequest = z.infer<typeof upsertProductivityTargetSchema>;

export interface WorkerProductivityRow {
  workerId: string;
  workerName: string;
  role: z.infer<typeof laborRoleSchema>;
  totalHours: number;
  daysWorked: number;
  avgHoursPerDay: number;
  estimatedOutput: number | null;
  targetPerHh: number | null;
  metricLabel: string | null;
}
